import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import BankAccountsPanel from "../components/BankAccountsPanel";
import InvoiceIdentitiesPanel from "../components/InvoiceIdentitiesPanel";
import { useAuth } from "../auth";

export default function Settings() {
  const { user: me } = useAuth();
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingVis, setSavingVis] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/google/status");
      setStatus(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Google status");
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Surface the result of the OAuth redirect, then clean the URL.
  const oauthResult = params.get("google");
  useEffect(() => {
    if (oauthResult) {
      load();
      const next = new URLSearchParams(params);
      next.delete("google");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [oauthResult]);

  const connect = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.assign(data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't start Google connect");
      setBusy(false);
    }
  };

  const setVisibility = async (visibility) => {
    if (savingVis || status?.sheet_visibility === visibility) return;
    setSavingVis(true);
    setErr(null);
    try {
      const { data } = await api.put("/google/sheet-visibility", { visibility });
      setStatus((s) => ({ ...s, sheet_visibility: data.sheet_visibility }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't update sheet visibility");
    } finally {
      setSavingVis(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post("/google/disconnect");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't disconnect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div className="title-block">
          <h1>Settings</h1>
        </div>
      </div>

      <div className="card pad" style={{ maxWidth: 640 }}>
        <h3 style={{ marginTop: 0 }}>Google Sheets</h3>
        <p className="muted" style={{ marginTop: 6 }}>
          Connect a Google account to auto-create one spreadsheet per project, with
          a tab per page. It's shared with the project's client as an editor (so
          their edits and notes come back for review), viewable by anyone with the
          link, and kept synced as you add tasks.
        </p>

        {oauthResult === "connected" && (
          <div className="banner success" style={{ marginTop: 12 }}>
            Google account connected.
          </div>
        )}
        {oauthResult === "error" && (
          <div className="error" style={{ marginTop: 12 }}>
            Google connection failed. Please try again.
          </div>
        )}
        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

        {status && !status.configured && (
          <div className="banner warn" style={{ marginTop: 12 }}>
            The Google integration isn't configured on the server yet
            (missing OAuth client / encryption key).
          </div>
        )}

        {status && status.configured && (
          <div style={{ marginTop: 16 }}>
            {status.connected ? (
              <div className="row-h" style={{ justifyContent: "space-between", gap: 12 }}>
                <span>
                  Connected{status.email ? <> as <strong>{status.email}</strong></> : null}.
                </span>
                <button onClick={disconnect} disabled={busy}>Disconnect</button>
              </div>
            ) : (
              <button className="primary" onClick={connect} disabled={busy}>
                {busy ? "Redirecting…" : "Connect Google account"}
              </button>
            )}
          </div>
        )}

        {/* Sheet visibility toggle disabled for now — every synced sheet is made
            public (anyone with the link can view) and no per-client sharing is
            applied. Uncomment to restore the public/private control.
        {status && status.configured && (
          <div className="vis-section">
            <h4 style={{ margin: "0 0 4px" }}>Sheet visibility</h4>
            <p className="muted" style={{ margin: "0 0 12px" }}>
              Who can open a synced sheet. Changing this updates every existing
              sheet too. Sheets only contain client-facing data (task, price,
              agreed, total) — no pay splits or internal notes.
            </p>
            <label className={`vis-option${status.sheet_visibility === "public" ? " active" : ""}`}>
              <input
                type="radio"
                name="sheet-visibility"
                checked={status.sheet_visibility === "public"}
                disabled={savingVis}
                onChange={() => setVisibility("public")}
              />
              <span>
                <strong>Public</strong> — anyone with the link can view
                <span className="muted block">
                  Link-only (not search-indexed). The client is still added so they can comment.
                </span>
              </span>
            </label>
            <label className={`vis-option${status.sheet_visibility === "private" ? " active" : ""}`}>
              <input
                type="radio"
                name="sheet-visibility"
                checked={status.sheet_visibility === "private"}
                disabled={savingVis}
                onChange={() => setVisibility("private")}
              />
              <span>
                <strong>Private</strong> — shared with the client only
                <span className="muted block">
                  Only the project's client can open the sheet (as a commenter).
                </span>
              </span>
            </label>
            {savingVis && <div className="muted" style={{ marginTop: 8 }}>Updating sheets…</div>}
          </div>
        )}
        */}
      </div>

      {/* Bank accounts are invoice data, so they sit behind the same super-admin
          check the invoice endpoints use. The server enforces it either way. */}
      {me?.role === "admin" && me?.is_superadmin && (
        <>
          <InvoiceIdentitiesPanel />
          <BankAccountsPanel />
        </>
      )}
    </>
  );
}
